<script>
	import { onMount } from 'svelte';
	import { crmAPI, contactsAPI } from './api.js';
	import { toast } from './lib/toast.js';
	import Icon from './lib/Icon.svelte';

	let pipelines = $state([]);
	let activePipelineId = $state(null);
	let leads = $state([]);
	let loading = $state(true);
	let error = $state('');

	let dragLeadId = $state(null);
	let dragOverStageId = $state(null);

	// Which leads to show in the board: only active, or include won/lost too.
	// Without this, a lead marked "Ganado"/"Perdido" vanishes from the board and
	// looks like it was never created.
	let statusFilter = $state('active'); // 'active' | 'all'

	// Multi-select / bulk
	let selectionMode = $state(false);
	let selectedIds = $state(new Set());
	let bulkBusy = $state(false);

	function toggleSelection(id) {
		const next = new Set(selectedIds);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		selectedIds = next;
	}
	function clearSelection() {
		selectedIds = new Set();
	}
	function selectAllInStage(stageId) {
		const next = new Set(selectedIds);
		for (const l of (leadsByStage[stageId] || [])) next.add(l.id);
		selectedIds = next;
	}

	let selectionCount = $derived(selectedIds.size);
	let selectedValue = $derived(
		leads.filter(l => selectedIds.has(l.id)).reduce((s, l) => s + (l.value || 0), 0)
	);

	async function bulkMove(stageId) {
		if (!selectionCount) return;
		bulkBusy = true;
		try {
			const res = await crmAPI.bulkLeads([...selectedIds], 'move', { stage_id: stageId });
			toast.success(`${res.affected} leads movidos`);
			clearSelection();
			await loadAll();
		} catch (err) { toast.error(err.message || 'No se pudo mover'); }
		finally { bulkBusy = false; }
	}
	async function bulkStatus(status) {
		if (!selectionCount) return;
		bulkBusy = true;
		try {
			const res = await crmAPI.bulkLeads([...selectedIds], 'status', { status });
			toast.success(`${res.affected} leads marcados ${status}`);
			clearSelection();
			await loadAll();
		} catch (err) { toast.error(err.message || 'No se pudo actualizar'); }
		finally { bulkBusy = false; }
	}
	async function bulkDelete() {
		if (!selectionCount) return;
		if (!confirm(`¿Eliminar ${selectionCount} leads? Esta acción no se puede deshacer.`)) return;
		bulkBusy = true;
		try {
			const res = await crmAPI.bulkLeads([...selectedIds], 'delete');
			toast.success(`${res.affected} leads eliminados`);
			clearSelection();
			await loadAll();
		} catch (err) { toast.error(err.message || 'No se pudo eliminar'); }
		finally { bulkBusy = false; }
	}
	async function bulkPriority(priority) {
		if (!selectionCount) return;
		bulkBusy = true;
		try {
			const res = await crmAPI.bulkLeads([...selectedIds], 'priority', { priority });
			toast.success(`Prioridad actualizada en ${res.affected} leads`);
			clearSelection();
			await loadAll();
		} catch (err) { toast.error(err.message || 'No se pudo actualizar'); }
		finally { bulkBusy = false; }
	}

	// Modals
	let showCreate = $state(false);
	let createStageId = $state(null);
	let newLead = $state({ title: '', value: '', contact_id: '', priority: 'medium', source: '' });
	let contactOptions = $state([]);

	let editingLead = $state(null); // full object when open
	let drawerTab = $state('details'); // details | timeline
	let activities = $state([]);
	let activitiesLoading = $state(false);
	let newNote = $state('');
	let newNoteKind = $state('note');

	const priorityColors = {
		low:    'badge-info',
		medium: 'badge',
		high:   'badge-warning',
		urgent: 'badge-danger'
	};

	let activePipeline = $derived(pipelines.find(p => p.id === activePipelineId) || null);

	let leadsByStage = $derived.by(() => {
		const map = {};
		if (!activePipeline) return map;
		for (const s of activePipeline.stages) map[s.id] = [];
		for (const l of leads) {
			if (statusFilter === 'active' && l.status !== 'active') continue;
			if (!map[l.stage_id]) map[l.stage_id] = [];
			map[l.stage_id].push(l);
		}
		// sort each by position then created
		for (const k of Object.keys(map)) {
			map[k].sort((a, b) => (a.position - b.position) || (a.id - b.id));
		}
		return map;
	});

	let totalActive = $derived(leads.filter(l => l.status === 'active').length);
	let totalValue = $derived(
		leads.filter(l => l.status === 'active').reduce((sum, l) => sum + (l.value || 0), 0)
	);

	async function loadAll() {
		loading = true;
		error = '';
		try {
			pipelines = await crmAPI.getPipelines();
			if (!pipelines.length) {
				// First load auto-creates default; refetch
				pipelines = await crmAPI.getPipelines();
			}
			if (!activePipelineId && pipelines.length) {
				activePipelineId = (pipelines.find(p => p.is_default) || pipelines[0]).id;
			}
			leads = await crmAPI.listLeads(activePipelineId);
		} catch (e) {
			error = e.message || 'Error cargando pipeline';
		} finally {
			loading = false;
		}
	}

	async function changePipeline(id) {
		activePipelineId = id;
		loading = true;
		try {
			leads = await crmAPI.listLeads(id);
		} finally {
			loading = false;
		}
	}

	function fmtMoney(v, currency = 'USD') {
		const n = Number(v) || 0;
		try {
			return new Intl.NumberFormat('en-US', {
				style: 'currency', currency, maximumFractionDigits: 0
			}).format(n);
		} catch { return `$${n.toFixed(0)}`; }
	}

	// ---------- Drag & drop ----------
	function onDragStart(e, lead) {
		dragLeadId = lead.id;
		e.dataTransfer.effectAllowed = 'move';
		try { e.dataTransfer.setData('text/plain', String(lead.id)); } catch {}
	}
	function onDragOver(e, stageId) {
		e.preventDefault();
		dragOverStageId = stageId;
		e.dataTransfer.dropEffect = 'move';
	}
	function onDragLeave(stageId) {
		if (dragOverStageId === stageId) dragOverStageId = null;
	}
	async function onDrop(e, stageId) {
		e.preventDefault();
		const id = dragLeadId;
		dragLeadId = null;
		dragOverStageId = null;
		if (!id) return;
		const lead = leads.find(l => l.id === id);
		if (!lead || lead.stage_id === stageId) return;

		// optimistic update
		const prevStage = lead.stage_id;
		lead.stage_id = stageId;
		leads = [...leads];

		try {
			const updated = await crmAPI.moveLead(id, stageId);
			Object.assign(lead, updated);
			leads = [...leads];
		} catch (err) {
			lead.stage_id = prevStage;
			leads = [...leads];
			toast.error(err.message || 'No se pudo mover el lead');
		}
	}

	// ---------- Create lead ----------
	async function openCreate(stageId) {
		createStageId = stageId;
		newLead = { title: '', value: '', contact_id: '', priority: 'medium', source: '' };
		showCreate = true;
		if (!contactOptions.length) {
			try {
				const res = await contactsAPI.getAll();
				contactOptions = res.contacts || res || [];
			} catch {}
		}
	}
	async function submitCreate(e) {
		e.preventDefault();
		try {
			const payload = {
				pipeline_id: activePipelineId,
				stage_id: createStageId,
				title: newLead.title,
				value: parseFloat(newLead.value) || 0,
				priority: newLead.priority,
				source: newLead.source || null,
				contact_id: newLead.contact_id || null
			};
			const created = await crmAPI.createLead(payload);
			leads = [...leads, created];
			showCreate = false;
			toast.success('Lead creado');
		} catch (err) {
			toast.error(err.message || 'No se pudo crear el lead');
		}
	}

	// ---------- Edit lead ----------
	async function openEdit(lead) {
		editingLead = { ...lead, value: lead.value ?? 0 };
		drawerTab = 'details';
		activities = [];
		await loadActivities(lead.id);
	}

	async function loadActivities(leadId) {
		activitiesLoading = true;
		try {
			activities = await crmAPI.listActivities(leadId);
		} catch (err) {
			toast.error(err.message || 'No se pudieron cargar las actividades');
		} finally {
			activitiesLoading = false;
		}
	}

	async function postNote() {
		const text = newNote.trim();
		if (!text || !editingLead) return;
		try {
			const created = await crmAPI.createActivity(editingLead.id, { kind: newNoteKind, text });
			activities = [created, ...activities];
			newNote = '';
			toast.success(newNoteKind === 'note' ? 'Nota agregada' : 'Actividad registrada');
		} catch (err) {
			toast.error(err.message || 'No se pudo guardar');
		}
	}

	async function removeActivity(act) {
		if (!editingLead) return;
		if (!confirm('¿Eliminar esta actividad?')) return;
		try {
			await crmAPI.deleteActivity(editingLead.id, act.id);
			activities = activities.filter(a => a.id !== act.id);
			toast.success('Actividad eliminada');
		} catch (err) {
			toast.error(err.message || 'No se pudo eliminar');
		}
	}

	const ACTIVITY_META = {
		created:      { icon: 'plus',         color: 'var(--accent)',     label: 'Lead creado' },
		note:         { icon: 'edit',         color: 'var(--text-2)',     label: 'Nota' },
		stage_change: { icon: 'arrow-right',  color: 'var(--info)',       label: 'Cambio de etapa' },
		status_change:{ icon: 'refresh',      color: 'var(--info)',       label: 'Cambio de estado' },
		won:          { icon: 'check-circle', color: 'var(--success)',    label: 'Ganado' },
		lost:         { icon: 'x',            color: 'var(--danger)',     label: 'Perdido' },
		reopened:     { icon: 'refresh',      color: 'var(--accent)',     label: 'Reabierto' },
		call:         { icon: 'phone',        color: 'var(--brand-dark)', label: 'Llamada' },
		email:        { icon: 'mail',         color: 'var(--brand-dark)', label: 'Email' },
		task:         { icon: 'check',        color: 'var(--warning)',    label: 'Tarea' }
	};
	async function saveEdit(e) {
		e.preventDefault();
		try {
			const payload = {
				title: editingLead.title,
				description: editingLead.description,
				value: parseFloat(editingLead.value) || 0,
				priority: editingLead.priority,
				source: editingLead.source,
				expected_close_date: editingLead.expected_close_date || null
			};
			const updated = await crmAPI.updateLead(editingLead.id, payload);
			const idx = leads.findIndex(l => l.id === updated.id);
			if (idx >= 0) leads[idx] = updated;
			leads = [...leads];
			editingLead = null;
			toast.success('Lead actualizado');
		} catch (err) {
			toast.error(err.message || 'No se pudo guardar');
		}
	}
	async function markAs(status) {
		if (!editingLead) return;
		try {
			const updated = await crmAPI.updateLead(editingLead.id, { status });
			const idx = leads.findIndex(l => l.id === updated.id);
			if (idx >= 0) leads[idx] = updated;
			leads = [...leads];
			editingLead = null;
			toast.success(status === 'won' ? '🎉 Lead ganado' : status === 'lost' ? 'Lead marcado perdido' : 'Lead reabierto');
		} catch (err) {
			toast.error(err.message || 'No se pudo actualizar');
		}
	}
	async function deleteLead() {
		if (!editingLead) return;
		if (!confirm('¿Eliminar este lead?')) return;
		try {
			await crmAPI.deleteLead(editingLead.id);
			leads = leads.filter(l => l.id !== editingLead.id);
			editingLead = null;
			toast.success('Lead eliminado');
		} catch (err) {
			toast.error(err.message || 'No se pudo eliminar');
		}
	}

	onMount(loadAll);
</script>

<div class="leads-view">
	<!-- Toolbar -->
	<div class="toolbar">
		<div class="toolbar-left">
			<select
				class="pipeline-select"
				value={activePipelineId}
				onchange={(e) => changePipeline(parseInt(e.target.value))}
				disabled={loading}
			>
				{#each pipelines as p}
					<option value={p.id}>{p.name}</option>
				{/each}
			</select>
			<div class="kpis">
				<div class="kpi-pill"><Icon name="kanban" size={13} /> {totalActive} activos</div>
				<div class="kpi-pill brand"><Icon name="trending-up" size={13} /> {fmtMoney(totalValue)}</div>
			</div>
			<div class="status-toggle">
				<button
					class="status-toggle-btn"
					class:active={statusFilter === 'active'}
					onclick={() => statusFilter = 'active'}
				>Activos</button>
				<button
					class="status-toggle-btn"
					class:active={statusFilter === 'all'}
					onclick={() => statusFilter = 'all'}
				>Todos</button>
			</div>
		</div>
		<div class="toolbar-right">
			<button
				class="btn btn-sm"
				class:btn-primary={selectionMode}
				class:btn-secondary={!selectionMode}
				onclick={() => { selectionMode = !selectionMode; if (!selectionMode) clearSelection(); }}
			>
				<Icon name={selectionMode ? 'check-circle' : 'list'} size={14} />
				{selectionMode ? 'Salir' : 'Seleccionar'}
			</button>
			<button class="btn btn-secondary btn-sm" onclick={loadAll} disabled={loading}>
				<Icon name="refresh" size={14} />
				Actualizar
			</button>
			<button
				class="btn btn-primary btn-sm"
				disabled={!activePipeline?.stages?.length}
				onclick={() => openCreate(activePipeline.stages[0].id)}
			>
				<Icon name="plus" size={14} />
				Nuevo lead
			</button>
		</div>
	</div>

	{#if selectionMode && selectionCount > 0}
		<div class="bulk-bar">
			<div class="bulk-info">
				<Icon name="check" size={14} />
				<b>{selectionCount}</b> seleccionados
				{#if selectedValue > 0}<span class="muted">· {fmtMoney(selectedValue)}</span>{/if}
			</div>
			<div class="bulk-actions">
				<select
					class="bulk-select"
					disabled={bulkBusy}
					onchange={(e) => { if (e.target.value) { bulkMove(parseInt(e.target.value)); e.target.value = ''; } }}
				>
					<option value="">Mover a etapa...</option>
					{#each activePipeline?.stages || [] as s}
						<option value={s.id}>{s.name}</option>
					{/each}
				</select>
				<select
					class="bulk-select"
					disabled={bulkBusy}
					onchange={(e) => { if (e.target.value) { bulkPriority(e.target.value); e.target.value = ''; } }}
				>
					<option value="">Prioridad...</option>
					<option value="low">Baja</option>
					<option value="medium">Media</option>
					<option value="high">Alta</option>
					<option value="urgent">Urgente</option>
				</select>
				<button class="btn btn-brand btn-sm" disabled={bulkBusy} onclick={() => bulkStatus('won')}>
					🎉 Ganado
				</button>
				<button class="btn btn-secondary btn-sm" disabled={bulkBusy} onclick={() => bulkStatus('lost')}>
					Perdido
				</button>
				<button class="btn btn-ghost btn-sm danger" disabled={bulkBusy} onclick={bulkDelete}>
					<Icon name="trash" size={13} /> Eliminar
				</button>
				<button class="btn btn-ghost btn-sm" onclick={clearSelection}>
					<Icon name="x" size={13} /> Limpiar
				</button>
			</div>
		</div>
	{/if}

	{#if error}
		<div class="error-banner">
			<Icon name="alert-circle" size={14} />
			{error}
			<button class="banner-close" onclick={() => (error = '')}><Icon name="x" size={14} /></button>
		</div>
	{/if}

	<!-- Board -->
	{#if loading}
		<div class="board-loading"><div class="spinner"></div></div>
	{:else if !activePipeline}
		<div class="empty">
			<Icon name="kanban" size={36} />
			<h3>Sin pipelines</h3>
			<p>Crea tu primer pipeline para empezar.</p>
		</div>
	{:else}
		<div class="board">
			{#each activePipeline.stages as stage (stage.id)}
				{@const stageLeads = leadsByStage[stage.id] || []}
				{@const stageValue = stageLeads.reduce((s, l) => s + (l.value || 0), 0)}
				<div
					class="column"
					class:drag-over={dragOverStageId === stage.id}
					style="--stage-color: {stage.color || 'var(--accent)'}"
					ondragover={(e) => onDragOver(e, stage.id)}
					ondragleave={() => onDragLeave(stage.id)}
					ondrop={(e) => onDrop(e, stage.id)}
					role="list"
					aria-label={stage.name}
				>
					<div class="col-head">
						<div class="col-title">
							<span class="name">{stage.name}</span>
							<span class="count">{stageLeads.length}</span>
							{#if selectionMode && stageLeads.length}
								<button class="select-all-btn" title="Seleccionar todos" onclick={() => selectAllInStage(stage.id)}>
									<Icon name="check" size={11} />
								</button>
							{/if}
							{#if stageValue > 0}
								<span class="col-value">{fmtMoney(stageValue)}</span>
							{/if}
						</div>
					</div>

					<div class="col-body">
						{#each stageLeads as lead (lead.id)}
							{@const isSelected = selectedIds.has(lead.id)}
							<button
								class="lead-card"
								class:dragging={dragLeadId === lead.id}
								class:selected={isSelected}
								draggable={!selectionMode}
								ondragstart={(e) => !selectionMode && onDragStart(e, lead)}
								onclick={() => selectionMode ? toggleSelection(lead.id) : openEdit(lead)}
								type="button"
							>
								{#if selectionMode}
									<span class="select-check" class:on={isSelected}>
										{#if isSelected}<Icon name="check" size={11} />{/if}
									</span>
								{/if}
								<div class="card-head">
									{#if lead.status === 'won'}
										<span class="badge badge-success">Ganado</span>
									{:else if lead.status === 'lost'}
										<span class="badge badge-danger">Perdido</span>
									{:else}
										<span class="badge {priorityColors[lead.priority] || 'badge'}">
											{lead.priority || 'medium'}
										</span>
									{/if}
									{#if lead.value > 0}
										<span class="card-value">{fmtMoney(lead.value, lead.currency)}</span>
									{/if}
								</div>
								<div class="card-title">{lead.title}</div>
								{#if lead.contact}
									<div class="card-contact">
										<div class="avatar-xs">{(lead.contact.name || '?').charAt(0)}</div>
										<span>{lead.contact.name}</span>
									</div>
								{/if}
								{#if lead.tags?.length}
									<div class="card-tags">
										{#each lead.tags.slice(0, 3) as t}<span class="tag">{t}</span>{/each}
									</div>
								{/if}
							</button>
						{/each}

						<button class="add-lead" onclick={() => openCreate(stage.id)} type="button">
							<Icon name="plus" size={14} />
							<span>Agregar lead</span>
						</button>
					</div>
				</div>
			{/each}
		</div>
	{/if}
</div>

<!-- ===== Create modal ===== -->
{#if showCreate}
	<div class="modal-back" onclick={() => (showCreate = false)} role="presentation">
		<div class="modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>Nuevo lead</h3>
				<button class="icon-btn" onclick={() => (showCreate = false)} aria-label="Cerrar">
					<Icon name="x" size={16} />
				</button>
			</div>
			<form onsubmit={submitCreate}>
				<label>Título *
					<input type="text" bind:value={newLead.title} required placeholder="Cliente interesado en..." />
				</label>
				<label>Contacto
					<select bind:value={newLead.contact_id}>
						<option value="">— Sin contacto —</option>
						{#each contactOptions as c}
							<option value={c.id}>{c.name}</option>
						{/each}
					</select>
				</label>
				<div class="form-row">
					<label>Valor estimado
						<input type="number" min="0" step="0.01" bind:value={newLead.value} placeholder="0" />
					</label>
					<label>Prioridad
						<select bind:value={newLead.priority}>
							<option value="low">Baja</option>
							<option value="medium">Media</option>
							<option value="high">Alta</option>
							<option value="urgent">Urgente</option>
						</select>
					</label>
				</div>
				<label>Origen
					<input type="text" bind:value={newLead.source} placeholder="WhatsApp, web, referido..." />
				</label>
				<div class="modal-foot">
					<button type="button" class="btn btn-secondary" onclick={() => (showCreate = false)}>Cancelar</button>
					<button type="submit" class="btn btn-primary">Crear lead</button>
				</div>
			</form>
		</div>
	</div>
{/if}

<!-- ===== Edit drawer ===== -->
{#if editingLead}
	<div class="modal-back" onclick={() => (editingLead = null)} role="presentation">
		<div class="drawer" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<div>
					<h3>{editingLead.title}</h3>
					<div class="drawer-sub">Lead #{editingLead.id} · {editingLead.status}</div>
				</div>
				<button class="icon-btn" onclick={() => (editingLead = null)} aria-label="Cerrar">
					<Icon name="x" size={16} />
				</button>
			</div>
			<div class="drawer-tabs">
				<button class:active={drawerTab === 'details'} onclick={() => (drawerTab = 'details')}>
					<Icon name="edit" size={13} /> Detalles
				</button>
				<button class:active={drawerTab === 'timeline'} onclick={() => (drawerTab = 'timeline')}>
					<Icon name="list" size={13} /> Timeline
					{#if activities.length}<span class="tab-count">{activities.length}</span>{/if}
				</button>
			</div>

			{#if drawerTab === 'timeline'}
				<div class="timeline-pane">
					<div class="note-composer">
						<select bind:value={newNoteKind} class="kind-select">
							<option value="note">📝 Nota</option>
							<option value="call">📞 Llamada</option>
							<option value="email">✉️ Email</option>
							<option value="task">✅ Tarea</option>
						</select>
						<input
							type="text"
							placeholder="Escribe una nota..."
							bind:value={newNote}
							onkeydown={(e) => { if (e.key === 'Enter' && newNote.trim()) postNote(); }}
						/>
						<button
							class="btn btn-primary btn-sm"
							onclick={postNote}
							disabled={!newNote.trim()}
						>
							<Icon name="plus" size={13} /> Agregar
						</button>
					</div>

					{#if activitiesLoading}
						<div class="loading-mini">Cargando...</div>
					{:else if !activities.length}
						<div class="empty-mini">
							<Icon name="list" size={24} />
							<p>Sin actividad todavía</p>
						</div>
					{:else}
						<ul class="timeline">
							{#each activities as a (a.id)}
								{@const meta = ACTIVITY_META[a.kind] || ACTIVITY_META.note}
								<li class="t-item">
									<div class="t-dot" style="background:{meta.color}">
										<Icon name={meta.icon} size={11} stroke="#fff" strokeWidth={2.5} />
									</div>
									<div class="t-content">
										<div class="t-row">
											<span class="t-kind">{meta.label}</span>
											<span class="t-time">{new Date(a.created_at).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
										</div>
										{#if a.text}<div class="t-text">{a.text}</div>{/if}
										{#if a.user}<div class="t-by">por {a.user.name}</div>{/if}
										{#if ['note','call','email','task'].includes(a.kind)}
											<button class="t-del" onclick={() => removeActivity(a)} title="Eliminar">
												<Icon name="trash" size={11} />
											</button>
										{/if}
									</div>
								</li>
							{/each}
						</ul>
					{/if}
				</div>
			{:else}
			<form onsubmit={saveEdit}>
				<label>Título *
					<input type="text" bind:value={editingLead.title} required />
				</label>
				<label>Descripción
					<textarea bind:value={editingLead.description} rows="3"></textarea>
				</label>
				<div class="form-row">
					<label>Valor
						<input type="number" min="0" step="0.01" bind:value={editingLead.value} />
					</label>
					<label>Prioridad
						<select bind:value={editingLead.priority}>
							<option value="low">Baja</option>
							<option value="medium">Media</option>
							<option value="high">Alta</option>
							<option value="urgent">Urgente</option>
						</select>
					</label>
				</div>
				<div class="form-row">
					<label>Cierre estimado
						<input type="date" bind:value={editingLead.expected_close_date} />
					</label>
					<label>Origen
						<input type="text" bind:value={editingLead.source} />
					</label>
				</div>
				{#if editingLead.contact}
					<div class="contact-info">
						<div class="avatar-xs">{(editingLead.contact.name || '?').charAt(0)}</div>
						<div>
							<div style="font-weight:600">{editingLead.contact.name}</div>
							<div class="muted">{editingLead.contact.email || editingLead.contact.phone || ''}</div>
						</div>
					</div>
				{/if}

				<div class="modal-foot space-between">
					<button type="button" class="btn btn-ghost" onclick={deleteLead}>
						<Icon name="trash" size={14} /> Eliminar
					</button>
					<div class="flex gap-2">
						{#if editingLead.status === 'active'}
							<button type="button" class="btn btn-secondary" onclick={() => markAs('lost')}>
								Marcar perdido
							</button>
							<button type="button" class="btn btn-brand" onclick={() => markAs('won')}>
								Marcar ganado
							</button>
						{:else}
							<button type="button" class="btn btn-secondary" onclick={() => markAs('active')}>
								Reabrir
							</button>
						{/if}
						<button type="submit" class="btn btn-primary">Guardar</button>
					</div>
				</div>
			</form>
			{/if}
		</div>
	</div>
{/if}

<style>
	.leads-view {
		height: 100%;
		display: flex;
		flex-direction: column;
		background: var(--surface-1);
	}

	.toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 14px 24px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.toolbar-left { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
	.toolbar-right { display: flex; gap: 8px; }

	.status-toggle {
		display: inline-flex;
		border: 1px solid var(--border-1);
		border-radius: 8px;
		overflow: hidden;
	}
	.status-toggle-btn {
		padding: 5px 12px;
		font-size: 12px;
		font-weight: 600;
		background: var(--surface-0);
		color: var(--text-2);
		border: none;
		cursor: pointer;
	}
	.status-toggle-btn + .status-toggle-btn { border-left: 1px solid var(--border-1); }
	.status-toggle-btn.active {
		background: var(--brand, #4f46e5);
		color: #fff;
	}
	.pipeline-select {
		width: auto;
		min-width: 180px;
		padding: 8px 32px 8px 12px;
		font-weight: 600;
	}

	.kpis { display: flex; gap: 8px; }
	.kpi-pill {
		display: inline-flex; align-items: center; gap: 6px;
		padding: 5px 10px;
		background: var(--surface-2);
		border-radius: 20px;
		font-size: 12px;
		font-weight: 600;
		color: var(--text-2);
	}
	.kpi-pill.brand { background: var(--brand-soft); color: var(--brand-dark); }

	/* Bulk bar */
	.bulk-bar {
		display: flex; align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 10px 24px;
		background: var(--accent-soft);
		border-bottom: 1px solid var(--border-1);
		flex-wrap: wrap;
	}
	.bulk-info {
		display: flex; align-items: center; gap: 6px;
		font-size: 13px;
		color: var(--accent);
	}
	.bulk-info b { color: var(--text-1); font-weight: 700; }
	.bulk-info .muted { color: var(--text-3); margin-left: 4px; }
	.bulk-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
	.bulk-select {
		width: auto !important;
		font-size: 12.5px !important;
		padding: 5px 22px 5px 10px !important;
		font-weight: 500;
	}
	.btn-ghost.danger { color: var(--danger); }
	.btn-ghost.danger:hover { background: var(--danger-soft); }

	.select-all-btn {
		margin-left: 4px;
		width: 18px; height: 18px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 4px;
		color: var(--accent);
		background: var(--accent-soft);
	}
	.select-all-btn:hover { background: var(--accent); color: #fff; }

	.lead-card.selected {
		border-color: var(--accent);
		box-shadow: 0 0 0 2px var(--accent-soft);
	}
	.select-check {
		position: absolute;
		top: 8px; right: 8px;
		width: 18px; height: 18px;
		border-radius: 4px;
		border: 1.5px solid var(--border-2);
		background: var(--surface-0);
		display: flex; align-items: center; justify-content: center;
		color: #fff;
		flex-shrink: 0;
	}
	.select-check.on {
		background: var(--accent);
		border-color: var(--accent);
	}
	.lead-card { position: relative; }

	.error-banner {
		display: flex; align-items: center; gap: 8px;
		background: var(--danger-soft);
		color: var(--danger);
		padding: 8px 16px;
		font-size: 13px;
		border-bottom: 1px solid var(--border-1);
	}
	.banner-close {
		margin-left: auto;
		display: flex; align-items: center; justify-content: center;
		width: 22px; height: 22px;
		border-radius: 4px;
		color: var(--danger);
	}
	.banner-close:hover { background: rgba(239, 68, 68, 0.15); }

	.board-loading {
		flex: 1;
		display: flex; align-items: center; justify-content: center;
	}
	.spinner {
		width: 32px; height: 32px;
		border: 3px solid var(--border-1);
		border-top-color: var(--accent);
		border-radius: 50%;
		animation: spin 700ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	.empty {
		flex: 1;
		display: flex; flex-direction: column; align-items: center; justify-content: center;
		gap: 8px;
		color: var(--text-3);
	}
	.empty h3 { color: var(--text-1); margin-top: 6px; }

	/* Board */
	.board {
		flex: 1;
		display: flex;
		gap: 14px;
		padding: 18px 24px 24px;
		overflow-x: auto;
		overflow-y: hidden;
		min-height: 0;
	}
	/* Kommo-style: white columns, colored top bar per stage */
	.column {
		flex: 0 0 280px;
		display: flex;
		flex-direction: column;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-top: 3px solid var(--stage-color, var(--accent));
		border-radius: var(--radius-md);
		box-shadow: var(--shadow-xs);
		max-height: 100%;
		min-height: 0;
		transition: background var(--transition-fast), box-shadow var(--transition-fast);
	}
	.column.drag-over {
		background: var(--accent-soft);
		box-shadow: inset 0 0 0 2px var(--accent);
	}
	.col-head {
		padding: 12px 14px;
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.col-title {
		display: flex; align-items: center; gap: 8px;
		font-size: 12px;
		font-weight: 600;
		color: var(--text-2);
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}
	.count {
		font-size: 11px;
		color: var(--text-3);
		font-weight: 600;
		background: var(--surface-2);
		padding: 1px 8px;
		border-radius: 9999px;
	}
	.col-value {
		margin-left: auto;
		font-size: 11px;
		color: var(--text-3);
		font-weight: 600;
		text-transform: none;
		letter-spacing: 0;
	}
	.col-body {
		flex: 1;
		overflow-y: auto;
		padding: 10px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.lead-card {
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		padding: 12px;
		text-align: left;
		display: flex; flex-direction: column; gap: 8px;
		cursor: grab;
		box-shadow: var(--shadow-xs);
		transition: border-color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast);
		width: 100%;
	}
	.lead-card:hover {
		border-color: var(--border-2);
		box-shadow: var(--shadow-md);
		transform: translateY(-1px);
	}
	.lead-card.dragging {
		opacity: 0.5;
		transform: rotate(2deg);
	}
	.card-head {
		display: flex; align-items: center; justify-content: space-between;
	}
	.card-value {
		font-size: 12px;
		font-weight: 600;
		color: var(--brand-dark);
	}
	.card-title {
		font-size: 13.5px;
		font-weight: 600;
		color: var(--text-1);
		line-height: 1.35;
	}
	.card-contact {
		display: flex; align-items: center; gap: 6px;
		font-size: 12px;
		color: var(--text-2);
	}
	.avatar-xs {
		width: 22px; height: 22px;
		border-radius: 50%;
		background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
		color: #fff;
		display: flex; align-items: center; justify-content: center;
		font-size: 11px;
		font-weight: 600;
	}
	.card-tags { display: flex; gap: 4px; flex-wrap: wrap; }
	.tag {
		font-size: 11px;
		padding: 2px 6px;
		border-radius: 4px;
		background: var(--surface-2);
		color: var(--text-2);
	}

	.add-lead {
		display: flex; align-items: center; justify-content: center;
		gap: 6px;
		padding: 8px;
		border: 1px dashed var(--border-2);
		border-radius: 8px;
		font-size: 12.5px;
		color: var(--text-3);
		font-weight: 500;
		transition: border-color var(--transition-fast), color var(--transition-fast), background var(--transition-fast);
	}
	.add-lead:hover {
		color: var(--text-1);
		border-color: var(--accent);
		background: var(--accent-soft);
	}

	/* ===== Modals ===== */
	.modal-back {
		position: fixed; inset: 0;
		background: rgba(15, 23, 42, 0.5);
		display: flex; align-items: center; justify-content: center;
		z-index: 100;
		animation: fadein 150ms ease-out;
	}
	@keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
	.modal {
		background: var(--surface-0);
		border-radius: 14px;
		box-shadow: var(--shadow-xl);
		width: 100%;
		max-width: 440px;
		max-height: 90vh;
		overflow-y: auto;
	}
	.drawer {
		background: var(--surface-0);
		border-radius: 14px;
		box-shadow: var(--shadow-xl);
		width: 100%;
		max-width: 540px;
		max-height: 90vh;
		overflow-y: auto;
	}
	.modal-head {
		display: flex; align-items: center; justify-content: space-between;
		padding: 16px 20px;
		border-bottom: 1px solid var(--border-1);
	}
	.modal-head h3 { font-size: 16px; }
	.icon-btn {
		width: 30px; height: 30px;
		border-radius: 6px;
		display: flex; align-items: center; justify-content: center;
		color: var(--text-3);
	}
	.icon-btn:hover { background: var(--surface-2); color: var(--text-1); }

	form { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
	form label { font-size: 12.5px; }
	.form-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 12px;
	}
	.contact-info {
		display: flex; align-items: center; gap: 10px;
		padding: 10px;
		background: var(--surface-1);
		border-radius: 8px;
	}
	.muted { font-size: 12px; color: var(--text-3); }
	.modal-foot {
		display: flex; gap: 8px;
		justify-content: flex-end;
		padding-top: 8px;
		margin-top: 4px;
		border-top: 1px solid var(--border-1);
		padding: 16px 0 0;
	}
	.modal-foot.space-between { justify-content: space-between; }

	/* Drawer tabs + timeline */
	.drawer-sub { font-size: 11px; color: var(--text-3); margin-top: 2px; text-transform: capitalize; }
	.drawer-tabs {
		display: flex;
		padding: 8px 16px 0;
		gap: 4px;
		border-bottom: 1px solid var(--border-1);
	}
	.drawer-tabs button {
		display: inline-flex; align-items: center; gap: 6px;
		padding: 8px 12px;
		font-size: 12.5px;
		font-weight: 600;
		color: var(--text-3);
		border-bottom: 2px solid transparent;
		margin-bottom: -1px;
	}
	.drawer-tabs button:hover { color: var(--text-1); }
	.drawer-tabs button.active { color: var(--accent); border-bottom-color: var(--accent); }
	.tab-count {
		background: var(--accent-soft);
		color: var(--accent);
		font-size: 10px;
		padding: 1px 6px;
		border-radius: 9999px;
	}

	.timeline-pane { padding: 16px 20px 20px; }
	.note-composer {
		display: flex; gap: 6px;
		margin-bottom: 14px;
	}
	.kind-select {
		width: auto !important;
		flex-shrink: 0;
		font-size: 13px !important;
		padding: 8px 28px 8px 10px !important;
	}
	.note-composer input { flex: 1; padding: 8px 10px; font-size: 13px; }
	.btn-sm { padding: 6px 12px !important; }

	.loading-mini, .empty-mini {
		text-align: center; padding: 24px;
		color: var(--text-3);
		font-size: 13px;
	}
	.empty-mini { display: flex; flex-direction: column; align-items: center; gap: 6px; }

	.timeline {
		list-style: none; padding: 0; margin: 0;
		display: flex; flex-direction: column;
		position: relative;
	}
	.timeline::before {
		content: '';
		position: absolute;
		left: 11px; top: 12px; bottom: 12px;
		width: 2px;
		background: var(--border-1);
	}
	.t-item {
		display: flex; gap: 14px;
		padding: 8px 0;
		position: relative;
	}
	.t-dot {
		width: 24px; height: 24px;
		border-radius: 50%;
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
		z-index: 1;
		border: 2px solid var(--surface-0);
	}
	.t-content {
		flex: 1; min-width: 0;
		padding: 8px 12px;
		background: var(--surface-1);
		border-radius: 8px;
		position: relative;
	}
	.t-row { display: flex; justify-content: space-between; gap: 8px; }
	.t-kind { font-size: 12px; font-weight: 600; color: var(--text-1); }
	.t-time { font-size: 11px; color: var(--text-3); }
	.t-text { font-size: 13px; color: var(--text-2); margin-top: 4px; line-height: 1.45; white-space: pre-wrap; }
	.t-by   { font-size: 11px; color: var(--text-3); margin-top: 4px; }
	.t-del {
		position: absolute; top: 4px; right: 4px;
		width: 22px; height: 22px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 4px;
		color: var(--text-3);
		opacity: 0;
		transition: opacity var(--transition-fast);
	}
	.t-content:hover .t-del { opacity: 1; }
	.t-del:hover { background: var(--danger-soft); color: var(--danger); }
</style>
